import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { disableAnalytics, loadAnalytics } from '@/lib/analytics';
import {
  COOKIE_CONSENT_UPDATED_EVENT,
  COOKIE_PREFERENCES_OPEN_EVENT,
  CookieConsentPreferences,
  readCookieConsent,
  saveCookieConsent,
} from '@/lib/cookieConsent';

const getDefaultDraft = (consent: CookieConsentPreferences | null) => ({
  analytics: consent?.analytics ?? false,
});

const CookieToggleRow = ({
  checked,
  description,
  disabled,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  description: string;
  disabled?: boolean;
  label: string;
  onCheckedChange?: (checked: boolean) => void;
}) => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900">{label}</p>
      <p className="mt-1 text-sm leading-5 text-slate-600">{description}</p>
    </div>
    <Switch
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      className="mt-1 data-[state=checked]:bg-[#20AEDC]"
    />
  </div>
);

const CookieConsentManager = () => {
  const [consent, setConsent] = useState<CookieConsentPreferences | null>(() => readCookieConsent());
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [draft, setDraft] = useState(() => getDefaultDraft(consent));
  const shouldShowBanner = !consent && !isPreferencesOpen;

  const preferenceSummary = useMemo(
    () => (draft.analytics ? 'Analytics cookies are allowed.' : 'Analytics cookies are blocked.'),
    [draft.analytics]
  );

  useEffect(() => {
    if (consent?.analytics) {
      loadAnalytics();
      return;
    }

    disableAnalytics();
  }, [consent]);

  useEffect(() => {
    const handleOpenPreferences = () => {
      const storedConsent = readCookieConsent();
      setConsent(storedConsent);
      setDraft(getDefaultDraft(storedConsent));
      setIsPreferencesOpen(true);
    };

    const handleConsentUpdated = (event: Event) => {
      const detail = (event as CustomEvent<CookieConsentPreferences>).detail;
      setConsent(detail);
      setDraft(getDefaultDraft(detail));
    };

    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);

    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
      window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);
    };
  }, []);

  const commitConsent = (analytics: boolean) => {
    const nextConsent = saveCookieConsent({ analytics });
    setConsent(nextConsent);
    setDraft(getDefaultDraft(nextConsent));
    setIsPreferencesOpen(false);
  };

  const openCustomize = () => {
    setDraft(getDefaultDraft(consent));
    setIsPreferencesOpen(true);
  };

  return (
    <>
      {shouldShowBanner && (
        <section
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-[120] border-t border-slate-200 bg-white shadow-[0_-1rem_2.5rem_rgba(15,23,42,0.14)]"
        >
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:py-5">
            <div className="max-w-3xl">
              <h2 className="text-base font-semibold text-slate-950">Cookie Preferences</h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                We use necessary cookies to keep CalorieScience working. Analytics tools, including
                Google Analytics, Google Tag Manager, Microsoft Clarity, Mixpanel, Meta Pixel, and
                LinkedIn Insight only load if you allow analytics cookies.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:min-w-[28rem]">
              <Button
                type="button"
                variant="outline"
                className="h-11 border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={() => commitConsent(false)}
              >
                Reject all
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11 border-[#20AEDC] text-[#0F7FA4] hover:bg-[#EEF9FD]"
                onClick={openCustomize}
              >
                Customize
              </Button>
              <Button
                type="button"
                className="h-11 bg-[#20AEDC] text-white hover:bg-[#178fb7]"
                onClick={() => commitConsent(true)}
              >
                Accept all
              </Button>
            </div>
          </div>
        </section>
      )}

      <Dialog open={isPreferencesOpen} onOpenChange={setIsPreferencesOpen}>
        <DialogContent className="max-h-[min(88vh,42rem)] max-w-[36rem] overflow-y-auto rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-950">Cookie Preferences</DialogTitle>
            <DialogDescription className="text-sm leading-6 text-slate-600">
              Choose which optional cookies CalorieScience can use. You can update this anytime
              from Cookie Preferences.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <CookieToggleRow
              checked
              disabled
              label="Strictly necessary cookies"
              description="Required for security, authentication, preferences, and core site features."
            />
            <CookieToggleRow
              checked={draft.analytics}
              label="Analytics cookies"
              description="Allows Google Analytics, Google Tag Manager, Microsoft Clarity, Mixpanel, Meta Pixel, and LinkedIn Insight to help us understand product usage and campaign performance."
              onCheckedChange={(analytics) => setDraft({ analytics })}
            />
          </div>

          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{preferenceSummary}</p>

          <DialogFooter className="gap-2 sm:flex-wrap sm:justify-between sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              onClick={() => commitConsent(false)}
            >
              Reject all
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                className="border-[#20AEDC] text-[#0F7FA4] hover:bg-[#EEF9FD]"
                onClick={() => commitConsent(draft.analytics)}
              >
                Save preferences
              </Button>
              <Button
                type="button"
                className="bg-[#20AEDC] text-white hover:bg-[#178fb7]"
                onClick={() => commitConsent(true)}
              >
                Accept all
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentManager;
